import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft,
  Play,
  UserPlus,
  Mic,
  FileText,
  CheckSquare,
  Download,
  Share2,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Clock,
  Users,
  Target,
  Lightbulb,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const tutorials = [
  {
    id: "registro",
    icon: UserPlus,
    title: "Registro y Verificación",
    duration: "2 min",
    steps: [
      {
        title: "Crear cuenta",
        description: "Haz clic en 'Registrarse' e ingresa tu nombre, correo electrónico y una contraseña segura."
      },
      {
        title: "Verificar correo",
        description: "Recibirás un código de 6 dígitos en tu correo. Ingrésalo para verificar tu cuenta."
      },
      {
        title: "¡Listo!",
        description: "Ya puedes iniciar sesión y comenzar a usar Minut-IA."
      }
    ]
  },
  {
    id: "grabar",
    icon: Mic,
    title: "Grabar una Reunión",
    duration: "3 min",
    highlight: true,
    steps: [
      {
        title: "Crear nueva reunión",
        description: "En el Dashboard, haz clic en el botón '+ Nueva Reunión'."
      },
      {
        title: "Ingresar título",
        description: "Dale un nombre descriptivo a tu reunión, por ejemplo: 'Reunión de planificación Q1'."
      },
      {
        title: "Grabar o subir audio",
        description: "Tienes dos opciones:\n• Grabar en vivo: Haz clic en el botón del micrófono y comienza a hablar.\n• Subir archivo: Arrastra un archivo de audio o haz clic para seleccionarlo."
      },
      {
        title: "Procesar reunión",
        description: "Haz clic en 'Procesar Reunión'. La IA transcribirá el audio y generará la minuta automáticamente."
      },
      {
        title: "Esperar procesamiento",
        description: "El proceso toma entre 1-5 minutos dependiendo de la duración. Verás el estado en tiempo real."
      }
    ]
  },
  {
    id: "minuta",
    icon: FileText,
    title: "Ver y Editar Minuta",
    duration: "2 min",
    steps: [
      {
        title: "Abrir reunión",
        description: "En el Dashboard, haz clic en cualquier reunión completada para ver su minuta."
      },
      {
        title: "Revisar contenido",
        description: "La minuta incluye:\n• Resumen ejecutivo\n• Participantes detectados\n• Tareas identificadas\n• Decisiones clave\n• Transcripción completa"
      },
      {
        title: "Reprocesar si es necesario",
        description: "Si la minuta no es precisa, haz clic en 'Reprocesar' para regenerarla."
      }
    ]
  },
  {
    id: "tareas",
    icon: CheckSquare,
    title: "Gestionar Tareas",
    duration: "2 min",
    highlight: true,
    steps: [
      {
        title: "Ver tareas pendientes",
        description: "En el Dashboard, baja hasta la sección 'Tareas Pendientes'. Haz clic para expandirla."
      },
      {
        title: "Cambiar estado",
        description: "Cada tarea tiene botones para cambiar su estado:\n• 'Iniciar' → Marca como en proceso\n• 'Completar' → Marca como terminada\n• 'Pendiente' → Regresa a pendiente"
      },
      {
        title: "Ver en contexto",
        description: "Haz clic en el nombre de la reunión para ir directamente a esa minuta y ver más detalles."
      },
      {
        title: "Dentro de la reunión",
        description: "También puedes gestionar tareas desde la vista de cada reunión en la sección 'Tareas'."
      }
    ]
  },
  {
    id: "descargar",
    icon: Download,
    title: "Descargar Minutas",
    duration: "1 min",
    steps: [
      {
        title: "Abrir reunión completada",
        description: "Ve a cualquier reunión que tenga estado 'Completada'."
      },
      {
        title: "Clic en Descargar",
        description: "Haz clic en el botón 'Descargar' en la parte superior derecha."
      },
      {
        title: "Elegir formato",
        description: "Selecciona el formato:\n• Word (.docx) - Documento profesional\n• Texto (.txt) - Texto plano"
      },
      {
        title: "Opciones adicionales",
        description: "También puedes descargar solo secciones específicas como 'Solo resumen' o 'Solo transcripción'."
      }
    ]
  },
  {
    id: "compartir",
    icon: Share2,
    title: "Compartir por Email",
    duration: "1 min",
    steps: [
      {
        title: "Abrir reunión",
        description: "Ve a la reunión que deseas compartir."
      },
      {
        title: "Clic en Compartir",
        description: "Haz clic en el botón 'Compartir' junto al botón de descarga."
      },
      {
        title: "Agregar destinatarios",
        description: "Ingresa los correos electrónicos. Puedes agregar hasta 10 destinatarios usando el botón '+ Agregar otro'."
      },
      {
        title: "Seleccionar secciones",
        description: "Elige qué secciones incluir en el correo: resumen, participantes, tareas, decisiones o transcripción."
      },
      {
        title: "Enviar",
        description: "Haz clic en 'Enviar'. Los destinatarios recibirán un correo profesional con la minuta."
      }
    ]
  },
  {
    id: "planes",
    icon: CreditCard,
    title: "Planes y Suscripción",
    duration: "2 min",
    steps: [
      {
        title: "Ver planes",
        description: "Haz clic en tu perfil (esquina superior derecha) y selecciona 'Ver planes' o ve directamente a /pricing."
      },
      {
        title: "Comparar opciones",
        description: "Compara los planes:\n• Gratuito: 1 reunión/mes, 20 min\n• Básico ($6.99/mes): 5 reuniones, 60 min\n• Pro ($13.99/mes): Ilimitadas, 120 min"
      },
      {
        title: "Elegir facturación",
        description: "Selecciona mensual o anual (13% de descuento)."
      },
      {
        title: "Suscribirse",
        description: "Haz clic en 'Suscribirse' y completa el pago con tarjeta a través de Stripe."
      },
      {
        title: "Activación inmediata",
        description: "Tu plan se activa inmediatamente después del pago exitoso."
      }
    ]
  },
  {
    id: "calendario",
    icon: Calendar,
    title: "Calendario y Sincronización",
    duration: "3 min",
    highlight: true,
    isPro: true,
    steps: [
      {
        title: "Acceder al Calendario (Solo Pro)",
        description: "El calendario está disponible exclusivamente en el plan Pro. Ve a la sección 'Calendario' desde el menú principal."
      },
      {
        title: "Crear un evento",
        description: "Haz clic en 'Nuevo Evento' o selecciona un slot vacío en el calendario. Llena el formulario con título, fecha, hora y detalles."
      },
      {
        title: "Recibir notificaciones",
        description: "Recibirás:\n• Correo de confirmación al crear el evento\n• Recordatorio automático 1 hora antes del evento"
      },
      {
        title: "Sincronizar con tu calendario",
        description: "Haz clic en 'Sincronizar con mi calendario', elige tu aplicación (Google, Outlook, Apple) y sigue el wizard paso a paso."
      },
      {
        title: "Tutorial completo",
        description: "Para instrucciones detalladas con imágenes, visita: /ayuda/calendario"
      }
    ]
  }
];

const TutorialCard = ({ tutorial, isExpanded, onToggle }) => {
  const Icon = tutorial.icon;
  
  return (
    <Card className={`overflow-hidden transition-all ${tutorial.highlight ? 'border-orange-300 bg-orange-50/50' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full text-left"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tutorial.highlight ? 'bg-orange-500 text-white' : 'bg-stone-100 text-stone-600'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900">{tutorial.title}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {tutorial.duration}
                  {tutorial.highlight && (
                    <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full ml-2">
                      Popular
                    </span>
                  )}
                  {tutorial.isPro && (
                    <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full ml-2 font-semibold">
                      PRO
                    </span>
                  )}
                </div>
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </CardContent>
      </button>
      
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t"
        >
          <div className="p-4 pt-2 space-y-4">
            {tutorial.steps.map((step, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-sm font-medium flex items-center justify-center">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-medium text-stone-800">{step.title}</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </Card>
  );
};

const Ayuda = () => {
  const [expandedTutorial, setExpandedTutorial] = useState("grabar");

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-orange-50/30 to-stone-100">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <span className="font-semibold text-xl text-stone-800">Minut-IA</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            Centro de Ayuda
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Aprende a usar todas las funciones de Minut-IA con nuestras guías paso a paso.
          </p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-10"
        >
          <Card className="text-center p-4">
            <Mic className="w-6 h-6 mx-auto mb-2 text-orange-500" />
            <p className="text-sm font-medium">Graba reuniones</p>
          </Card>
          <Card className="text-center p-4">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-orange-500" />
            <p className="text-sm font-medium">IA genera minutas</p>
          </Card>
          <Card className="text-center p-4">
            <Share2 className="w-6 h-6 mx-auto mb-2 text-orange-500" />
            <p className="text-sm font-medium">Comparte fácilmente</p>
          </Card>
        </motion.div>

        {/* Tutorials List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <h2 className="text-xl font-semibold text-stone-800 mb-4">Tutoriales</h2>
          
          {tutorials.map((tutorial) => (
            <TutorialCard
              key={tutorial.id}
              tutorial={tutorial}
              isExpanded={expandedTutorial === tutorial.id}
              onToggle={() => setExpandedTutorial(
                expandedTutorial === tutorial.id ? null : tutorial.id
              )}
            />
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <Card className="p-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <h3 className="text-xl font-semibold mb-2">¿Listo para empezar?</h3>
            <p className="text-orange-100 mb-4">
              Crea tu primera minuta en menos de 5 minutos.
            </p>
            <Link to="/new">
              <Button variant="secondary" className="bg-white text-orange-600 hover:bg-orange-50">
                <Play className="w-4 h-4 mr-2" />
                Crear mi primera reunión
              </Button>
            </Link>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default Ayuda;
